import { useCallback, useContext, useMemo, useState } from "react";
import Section from "../../parts/Section";
import Api, { AuthorizationsListResponse } from "../../Api";
import EntitySearch from "../../parts/EntitySearch";
import EntityList from "../../parts/EntityList";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getAmounts, intToDec } from "../../utils";
import classNames from "classnames";
import _ from "lodash";
import { Amounts, Quote, QuoteNote, QuoteTasks, TaskFilter } from "../../types";
import { SessionContext } from "../../SessionContext";
import { useSearchParams } from "react-router-dom";

type TaskItemIcons =
  | "signature"
  | "receipt"
  | "bag-shopping"
  | "gears"
  | "cash-register"
  | "sack-dollar";

const green = "bg-green-100 text-green-700";
const yellow = "bg-yellow-100 text-yellow-600";
const red = "bg-red-100 text-red-700";

const AuthorizationList = () => {
  const [data, setData] = useState<AuthorizationsListResponse[] | null>(null);
  const [count, setCount] = useState<number | null>(null);

  const [searchParams] = useSearchParams();
  const taskFilter = useMemo(
    () => ({
      signature: searchParams.get("signature") as "true" | "false" | null,
      order: searchParams.get("order") as "true" | "false" | null,
      equipment: searchParams.get("equipment") as "true" | "false" | null,
      install: searchParams.get("install") as "true" | "false" | null,
      billing: searchParams.get("billing") as "true" | "false" | null,
      commission: searchParams.get("commission") as "true" | "false" | null,
    }),
    [searchParams]
  );

  const searchFunction = useCallback(
    async (searchTerm: string, page: string) => {
      const filter = {
        signature:
          taskFilter.signature === null
            ? taskFilter.signature
            : taskFilter.signature === "true",
        order:
          taskFilter.order === null
            ? taskFilter.order
            : taskFilter.order === "true",
        equipment:
          taskFilter.equipment === null
            ? taskFilter.equipment
            : taskFilter.equipment === "true",
        install:
          taskFilter.install === null
            ? taskFilter.install
            : taskFilter.install === "true",
        billing:
          taskFilter.billing === null
            ? taskFilter.billing
            : taskFilter.billing === "true",
        commission:
          taskFilter.commission === null
            ? taskFilter.commission
            : taskFilter.commission === "true",
      };
      return Api.authorizationsList(
        JSON.stringify(filter),
        searchTerm,
        page
      ).then((response) => {
        setCount(response.pendingCount);
        return response;
      });
    },
    [taskFilter]
  );

  return (
    <>
      <div className="border-b">
        <Section className="p-4">
          <EntitySearch searchFunction={searchFunction} setEntities={setData} />
        </Section>
      </div>

      <TaskFilterInput currentFilter={taskFilter} />

      {data && (
        <Count
          count={count!}
          hasFilter={Object.values(taskFilter).some((e) => e !== null)}
        />
      )}

      <Section>
        <EntityList entities={data} Card={Authorization} />
      </Section>
    </>
  );
};

export default AuthorizationList;

const Authorization = ({
  entity: { quote, clientName, sellerName },
  expanded,
  setExpanded,
}: {
  entity: AuthorizationsListResponse;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}) => {
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState(quote.tasks);
  const [notes, setNotes] = useState(quote.notes);
  const [isCancelled, setIsCancelled] = useState(quote.isCancelled);

  const check = (key: keyof QuoteTasks) => () => {
    const updatedTasks = {
      ...tasks,
      [key]: !tasks[key],
    };
    setSaving(true);
    Api.authorizationsSetTasks(quote.uuid, updatedTasks).then(() => [
      setTasks(updatedTasks),
      setSaving(false),
    ]);
  };

  const startTasks = () => {
    const newTasks = {
      signature: false,
      order: false,
      equipment: false,
      install: false,
      billing: false,
      commission: false,
    };
    Api.authorizationsSetTasks(quote.uuid, newTasks).then(() =>
      setTasks(newTasks)
    );
  };

  const addNote = (text: string) =>
    Api.authorizationsAddNote(quote.uuid, text).then(setNotes);

  const deleteNote = (index: number) =>
    Api.authorizationsDeleteNote(quote.uuid, index).then(setNotes);

  const changeIsCancelled = () =>
    Api.authorizationsSetIsCancelled(quote.uuid, !isCancelled).then(() => [
      setIsCancelled(!isCancelled),
    ]);

  return (
    <div className="border rounded-lg shadow-sm">
      <div
        className={classNames(
          "p-4 hover:bg-slate-50 cursor-pointer flex flex-col gap-2 rounded-t-lg",
          { "rounded-b-lg": !expanded }
        )}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex gap-1">
          <Badge className="font-mono cursor-default">{quote.number}</Badge>

          <Badge>{quote.authorizedByClient ? "Cliente" : "Manual"}</Badge>

          <div className="flex-1"></div>

          {notes.length > 0 && (
            <Badge>
              <span className="flex gap-1 items-center">
                <FontAwesomeIcon icon="comment" className="text-gray-400" />
                <span>{notes.length}</span>
              </span>
            </Badge>
          )}

          {tasks && !isCancelled && <CompletedTasks tasks={tasks} />}

          {isCancelled && <Badge className={red}>Cancelado</Badge>}

          <NotificationButton quote={quote} />
        </div>

        <div className="overflow-scroll whitespace-nowrap no-scrollbar font-semibold">
          {clientName}
        </div>

        <AmountsDisplay amounts={getAmounts(quote)} />
      </div>

      {!expanded && (
        <Action
          icon="check-circle"
          label="Autorizado"
          date={quote.authorizationDate}
        />
      )}

      {expanded && (
        <div>
          {!tasks && !isCancelled && (
            <div
              className="border-t p-4 text-center text-sm font-semibold bg-yellow-100 text-yellow-600 cursor-pointer"
              onClick={startTasks}
            >
              Adicionar tarefas
            </div>
          )}

          {tasks && (
            <Tasks
              tasks={tasks}
              onChange={check}
              disabled={saving || isCancelled}
            />
          )}

          <Notes notes={notes} onAdd={addNote} onDelete={deleteNote} />

          {isCancelled && (
            <Action
              icon="ban"
              label="Cancelado"
              date={quote.cancellationDate}
            />
          )}

          <Action
            icon="check-circle"
            label="Autorizado"
            date={quote.authorizationDate}
          />

          <Action icon="envelope" label="Aprovado" date={quote.approvalDate} />

          <Action icon="file-lines" label="Criado" date={quote.creationDate} />

          <Action icon="user" label={sellerName} date={null} />

          <div className="flex border-t items-center">
            <button
              className="text-sm font-bold py-2 px-3 rounded-bl-lg text-red-700 hover:bg-red-50 basis-1/2 border-r"
              onClick={changeIsCancelled}
            >
              {isCancelled ? "Reativar" : "Cancelar"}
            </button>

            <button
              className="text-sm font-bold py-2 px-3 rounded-br-lg hover:bg-slate-50 basis-1/2"
              onClick={() =>
                window.open(`/externo/proposta/${quote.externalHash}`)
              }
            >
              Abrir orçamento
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const CompletedTasks = ({ tasks }: { tasks: QuoteTasks }) => (
  <span
    className={classNames(
      "text-xs font-bold py-0.5 px-2 rounded-full flex items-center gap-2",
      {
        [green]: Object.values(tasks).every((c) => c),
        [yellow]: !Object.values(tasks).every((c) => c),
      }
    )}
  >
    <FontAwesomeIcon icon="star" />
    <span>
      {Object.values(tasks).filter((c) => c).length}
      {" / "}
      {Object.keys(tasks).length}
    </span>
  </span>
);

const NotificationButton = ({ quote }: { quote: Quote }) => {
  const { user } = useContext(SessionContext);
  const [enabled, setEnabled] = useState(_.includes(quote.push, user!.uuid));
  const [loading, setLoading] = useState(false);

  const togglePush = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    setEnabled(!enabled);
    Api.authorizationsTogglePush(quote.uuid).then(() => setLoading(false));
  };

  return (
    <>
      {enabled && (
        <button
          className="text-xs bg-green-100 hover:bg-green-200 text-green-700 flex items-center justify-center p-1 rounded-full w-6 h-6"
          onClick={togglePush}
          disabled={loading}
        >
          <FontAwesomeIcon icon="bell" />
        </button>
      )}

      {!enabled && (
        <button
          className="text-xs bg-gray-50 hover:bg-gray-100 text-gray-500 flex items-center justify-center p-1 rounded-full w-6 h-6"
          onClick={togglePush}
          disabled={loading}
        >
          <FontAwesomeIcon icon="bell-slash" />
        </button>
      )}
    </>
  );
};

const Badge = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <span
    className={`text-xs font-bold text-gray-500 bg-gray-100 py-0.5 px-2 rounded-full ${className} flex items-center gap-1`}
    onClick={(e) => e.stopPropagation()}
  >
    {children}
  </span>
);

type ActionIcons = "check-circle" | "ban" | "envelope" | "file-lines" | "user";
const Action = ({
  icon,
  label,
  date,
}: {
  icon: ActionIcons;
  label: string;
  date: Date | null;
}) => (
  <div className="text-gray-400 truncate text-xs font-light border-t px-4 py-1">
    <span className="font-semibold text-gray-500">
      <FontAwesomeIcon icon={icon} className="text-gray-400 w-3 mr-3" />
      {label}
    </span>
    {date && (
      <>
        {" "}
        em {moment(date).format("DD/MM/YY")} às {moment(date).format("HH:mm")}
      </>
    )}
  </div>
);

const AmountsDisplay = ({ amounts }: { amounts: Amounts }) => (
  <div className="flex gap-1">
    {amounts.finalSale > 0 && (
      <div className="bg-cyan-100 text-cyan-800 rounded text-xs px-1.5 py-0.5 tracking-tighter">
        <span className="font-light">Venda</span>{" "}
        <span className="font-bold">{intToDec(amounts.finalSale)}</span>
      </div>
    )}
    {amounts.finalRent > 0 && (
      <div className="bg-lime-100 text-lime-800 rounded text-xs px-1.5 py-0.5 tracking-tighter">
        <span className="font-light">Mensal</span>{" "}
        <span className="font-bold">{intToDec(amounts.finalRent)}</span>
      </div>
    )}
    {amounts.fee > 0 && (
      <div className="bg-violet-100 text-violet-900 rounded text-xs px-1.5 py-0.5 tracking-tighter">
        <span className="font-light">Taxa</span>{" "}
        <span className="font-bold">{intToDec(amounts.fee)}</span>
      </div>
    )}
  </div>
);

const TaskFilterInput = ({ currentFilter }: { currentFilter: TaskFilter }) => {
  const [filters, setFilters] = useState(currentFilter);

  const [searchParams, setSearchParams] = useSearchParams();
  const currentTaskFilter = {
    signature: searchParams.get("signature"),
    order: searchParams.get("order"),
    equipment: searchParams.get("equipment"),
    install: searchParams.get("install"),
    billing: searchParams.get("billing"),
    commission: searchParams.get("commission"),
  };

  const setFilter =
    (key: keyof TaskFilter) => (value: "true" | "false" | null) =>
      setFilters((filters) => ({ ...filters, [key]: value }));

  return (
    <Section className="px-4 pt-2 flex gap-1 text-sm overflow-scroll no-scrollbar">
      <TaskFilterItem
        icon={"signature"}
        value={filters.signature}
        onClick={setFilter("signature")}
      />
      <TaskFilterItem
        icon={"receipt"}
        value={filters.order}
        onClick={setFilter("order")}
      />
      <TaskFilterItem
        icon={"bag-shopping"}
        value={filters.equipment}
        onClick={setFilter("equipment")}
      />
      <TaskFilterItem
        icon={"gears"}
        value={filters.install}
        onClick={setFilter("install")}
      />
      <TaskFilterItem
        icon={"cash-register"}
        value={filters.billing}
        onClick={setFilter("billing")}
      />
      <TaskFilterItem
        icon={"sack-dollar"}
        value={filters.commission}
        onClick={setFilter("commission")}
      />

      <button
        className="py-2 px-3 rounded font-extrabold text-white bg-sky-600 hover:bg-sky-500 disabled:bg-gray-100 disabled:text-gray-400"
        disabled={_.isEqual(filters, currentTaskFilter)}
        onClick={() => {
          setSearchParams((params) => {
            filters.signature === null
              ? params.delete("signature")
              : params.set("signature", filters.signature);
            filters.order === null
              ? params.delete("order")
              : params.set("order", filters.order);
            filters.equipment === null
              ? params.delete("equipment")
              : params.set("equipment", filters.equipment);
            filters.install === null
              ? params.delete("install")
              : params.set("install", filters.install);
            filters.billing === null
              ? params.delete("billing")
              : params.set("billing", filters.billing);
            filters.commission === null
              ? params.delete("commission")
              : params.set("commission", filters.commission);
            return params;
          });
        }}
      >
        Filtrar
      </button>
    </Section>
  );
};

const TaskFilterItem = ({
  icon,
  value,
  onClick,
}: {
  icon: TaskItemIcons;
  value: "true" | "false" | null;
  onClick: (value: "true" | "false" | null) => void;
}) => {
  const change = () => {
    if (value === null) onClick("false");
    if (value === "false") onClick("true");
    if (value === "true") onClick(null);
  };

  return (
    <div
      className={classNames(
        "py-2 px-3 rounded cursor-pointer border border-transparent",
        {
          "bg-gray-100 text-gray-500": value === null,
          [yellow]: value === "false",
          [green]: value === "true",
        }
      )}
      onClick={change}
    >
      {icon && <FontAwesomeIcon icon={icon} className="w-4" />}
    </div>
  );
};

const Tasks = ({
  tasks,
  onChange,
  disabled,
}: {
  tasks: QuoteTasks;
  onChange: (key: keyof QuoteTasks) => () => void;
  disabled: boolean;
}) => (
  <div className="flex flex-col">
    <Tasktem
      label="Contrato assinado"
      icon="signature"
      checked={tasks.signature}
      onChange={onChange("signature")}
      disabled={disabled}
    />

    <Tasktem
      label="Ordem de serviço emitida"
      icon="receipt"
      checked={tasks.order}
      onChange={onChange("order")}
      disabled={disabled}
    />

    <Tasktem
      label="Material despachado"
      icon="bag-shopping"
      checked={tasks.equipment}
      onChange={onChange("equipment")}
      disabled={disabled}
    />

    <Tasktem
      label="Instalação finalizada"
      icon="gears"
      checked={tasks.install}
      onChange={onChange("install")}
      disabled={disabled}
    />

    <Tasktem
      label="Financeiro lançado"
      icon="cash-register"
      checked={tasks.billing}
      onChange={onChange("billing")}
      disabled={disabled}
    />

    <Tasktem
      label="Comissão paga"
      icon="sack-dollar"
      checked={tasks.commission}
      onChange={onChange("commission")}
      disabled={disabled}
    />
  </div>
);

const Tasktem = ({
  label,
  icon,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  icon: TaskItemIcons;
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
}) => (
  <div
    className={classNames(
      "py-1.5 px-4 flex items-center gap-3 text-sm font-semibold border-t",
      {
        [green]: checked,
        [yellow]: !checked,
        "opacity-60": disabled,
        "cursor-pointer": !disabled,
      }
    )}
    onClick={() => !disabled && onChange()}
  >
    <FontAwesomeIcon className="w-4" icon={icon} />
    <div className="flex-1">{label}</div>
    <FontAwesomeIcon className="w-4" icon={checked ? "check" : "xmark"} />
  </div>
);

const Notes = ({
  notes,
  onAdd,
  onDelete,
}: {
  notes: QuoteNote[];
  onAdd: (text: string) => Promise<void>;
  onDelete: (index: number) => void;
}) => {
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState("");

  const addNote = (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    onAdd(newNote).then(() => [setNewNote(""), setSaving(false)]);
  };

  const deleteNote = (i: number) => () => {
    if (window.confirm("Apagar anotação?")) {
      onDelete(i);
    }
  };

  return (
    <div className="p-4 flex flex-col gap-2 border-t">
      {notes.map((note, i) => (
        <div
          key={i}
          className="border rounded-2xl pt-0.5 pb-1 px-2.5 text-sm leading-snug"
        >
          <div className="text-xs flex text-gray-500 mb-1">
            <span className="font-bold mr-1">{note.user}</span>
            <span className="font-light flex-1">
              {moment(note.date).format("DD/MM/YY")}
            </span>
            <button
              className="px-1 -mr-1"
              onClick={deleteNote(i)}
              disabled={saving}
            >
              ×
            </button>
          </div>
          <div>{note.text}</div>
        </div>
      ))}

      {notes.length === 0 && (
        <div className="text-center py-3 text-sm text-gray-400 italic">
          Nenhuma anotação
        </div>
      )}

      <form onSubmit={addNote}>
        <input
          className="border rounded-full w-full text-sm py-1 px-3 disabled:text-gray-500"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          disabled={saving}
          placeholder="Anotação..."
        />
      </form>
    </div>
  );
};

const Count = ({ count, hasFilter }: { count: number; hasFilter: boolean }) => {
  if (!count) return <></>;

  const text = hasFilter
    ? count === 1
      ? "resultado com pendência"
      : "resultados com pendência"
    : count === 1
      ? "autorização com pendência"
      : "autorizações com pendência";

  const bgLight =
    count <= 10
      ? "bg-green-50 text-green-800"
      : count <= 20
        ? "bg-yellow-100 text-yellow-600"
        : "bg-red-50 text-red-700";

  const bgBadge =
    count <= 10 ? "bg-green-600" : count <= 20 ? "bg-yellow-600" : "bg-red-600";

  return (
    <Section className="px-4 mt-2.5 -mb-1.5 text-sm text-center">
      <div className={`${bgLight} rounded p-1`}>
        <span
          className={`${bgBadge} font-bold text-white rounded-full py-0 px-1 mr-2`}
        >
          {count}
        </span>
        <span className="font-semibold">{text}</span>
      </div>
    </Section>
  );
};
